import { Button, dateToIsoDateOnly, MuiAlert, MuiRadioGroup, ResponsiveDrawer, Spin, strIsNullOrEmpty, Typography } from '@fox/atoms';
import { PAYMENT_SELECTOR_PREFIX, PaymentSelectorProps } from '@fox/finance';
import { FlexColumnSpaced, FlexRowSpaced, Padded, SpaceBetweenRow } from '@fox/layout';
import { Extension } from 'fox-extensions';
import moment from 'moment';
import { nanoid } from 'nanoid';
import React, { useState } from 'react';
import { useContentContext } from '../../../content-context';
import { useFinancialContext } from '../../../financial-config-context';
import { REFETCH_FINANCIAL, REFETCH_INVOICES, REFETCH_RECEIPTS } from '../../../topics';
import { usePayInvoice } from '../../mutation-hooks/mutation-implementations';
import { useInvoice } from '../../query-hooks/query-implementations';
import { CreateLineItemModelInput, SwiftFoxInvoice } from '../contracts';
import {
	convertMemberPortalCapabilityToSfEnumValue,
	convertMemberPortalToSfMethodEnumValue,
	formatCurrency,
	ReceiptStatusChip,
	renderDate,
	TABLE_BUTTON_STYLE
} from '../shared';
import { WebPaymentType } from '../subscriptions/renew-subscription';

export function PayInvoiceDesktop({
	record,
	useLinkedOrganisationData,
	allowedProviders
}: {
	record: SwiftFoxInvoice;
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const [open, setOpen] = useState<boolean>(false);

	return (
		<>
			<Button variant="primary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				Pay invoice
			</Button>
			<ResponsiveDrawer
				placement="right"
				title="Pay invoice"
				width={500}
				open={open}
				destroyOnClose
				onClose={() => {
					setOpen(false);
				}}
			>
				<PayInvoice
					invoice={record}
					closeDrawer={() => setOpen(false)}
					useLinkedOrganisationData={useLinkedOrganisationData}
					allowedProviders={allowedProviders}
				/>
			</ResponsiveDrawer>
		</>
	);
}

export function PayInvoiceMobile({
	record,
	useLinkedOrganisationData,
	allowedProviders
}: {
	record: SwiftFoxInvoice;
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const [open, setOpen] = useState<boolean>(false);

	return (
		<>
			<Button variant="primary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				Pay invoice
			</Button>
			<ResponsiveDrawer
				placement="bottom"
				title="Pay invoice"
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				destroyOnClose
				open={open}
				onClose={() => {
					setOpen(false);
				}}
			>
				<PayInvoice
					invoice={record}
					closeDrawer={() => setOpen(false)}
					useLinkedOrganisationData={useLinkedOrganisationData}
					allowedProviders={allowedProviders}
				/>
			</ResponsiveDrawer>
		</>
	);
}

export function PayInvoice({
	invoice,
	closeDrawer,
	useLinkedOrganisationData,
	allowedProviders
}: {
	invoice: SwiftFoxInvoice;
	closeDrawer: () => void;
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const { contentId, requestToken } = useContentContext();
	const { providers, customerError, customerLoading, getCustomers } = useFinancialContext();
	const customerData = getCustomers(useLinkedOrganisationData);

	const providersToUse = allowedProviders?.length
		? providers?.filter(p => allowedProviders.includes(p.provider.toLowerCase()))
		: providers;

	const {
		loading: payInvoiceLoading,
		error: payInvoiceError,
		mutate: payInvoice,
		data: payInvoiceResult
	} = usePayInvoice({
		requestToken,
		topics: [REFETCH_RECEIPTS, REFETCH_FINANCIAL, REFETCH_INVOICES]
	});
	const {
		loading: invoiceDetailedLoading,
		error: invoiceDetailedError,
		data: invoiceDetailed
	} = useInvoice({ contentId, invoiceId: invoice.id, requestToken });

	const [payment, setPayment] = useState<WebPaymentType>();

	if (!strIsNullOrEmpty(customerError) || !strIsNullOrEmpty(invoiceDetailedError))
		return <MuiAlert severity="error">{customerError || invoiceDetailedError}</MuiAlert>;
	const paymentResult = payInvoiceResult?.paymentResult;
	const invoiceResult = payInvoiceResult?.invoiceResult;
	const numberOfPaymentMethods = customerData.flatMap(c => c.creditCards).length + customerData.flatMap(c => c.directDebits).length;
	const hasPaymentMethods = numberOfPaymentMethods > 0;

	if (!hasPaymentMethods)
		return (
			<MuiAlert severity="warning">
				You do not have any payment methods available, please add a payment method and try again.
			</MuiAlert>
		);
	return (
		<Padded>
			<FlexColumnSpaced>
				{!payInvoiceError && paymentResult?.id ? (
					<FlexColumnSpaced>
						<SpaceBetweenRow>
							<Typography style={{ fontWeight: 'bold' }}>Receipt number:</Typography>
							<Typography>{paymentResult.receiptNo}</Typography>
						</SpaceBetweenRow>
						<SpaceBetweenRow>
							<Typography style={{ fontWeight: 'bold' }}>Transaction reference</Typography>
							<Typography>
								<Typography>{paymentResult.transactionRef}</Typography>
							</Typography>
						</SpaceBetweenRow>
						<SpaceBetweenRow>
							<Typography style={{ fontWeight: 'bold' }}>Date: </Typography>
							<Typography>{renderDate(paymentResult.createdOn)}</Typography>
						</SpaceBetweenRow>
						<SpaceBetweenRow>
							<Typography style={{ fontWeight: 'bold' }}>Status</Typography>
							<ReceiptStatusChip receipt={paymentResult} />
						</SpaceBetweenRow>
					</FlexColumnSpaced>
				) : (
					<>
						<Spin spinning={customerLoading || invoiceDetailedLoading}>
							{providersToUse?.length > 0 ? (
								<MuiRadioGroup
									value={JSON.stringify(payment) ?? null}
									name="payment"
									onChange={(_, value) => {
										const newValue = JSON.parse(value);

										setPayment(
											newValue.creditCardId
												? {
														creditCardId: newValue.creditCardId,
														method: newValue.method,
														provider: newValue.provider
												  }
												: newValue.directDebitId
												? {
														directDebitId: newValue.directDebitId,
														method: newValue.method,
														provider: newValue.provider
												  }
												: undefined
										);
									}}
								>
									{providersToUse?.map((c, i) => {
										const provider = {
											...c,
											capabilities: c.capabilities.map(capability =>
												convertMemberPortalCapabilityToSfEnumValue(capability)
											)
										};
										return (
											<React.Fragment key={`${c.provider}${c.method}`}>
												<Extension<PaymentSelectorProps<WebPaymentType>>
													name={`${PAYMENT_SELECTOR_PREFIX}.${convertMemberPortalToSfMethodEnumValue(c.method)}`}
													props={{
														customers: customerData,
														provider,
														selectedPayment: payment,
														setSelectedPayment: selectedPayment => {
															setPayment(selectedPayment);
														},
														meta: {
															useLinkedOrganisationData: useLinkedOrganisationData
														}
													}}
												/>
											</React.Fragment>
										);
									})}
								</MuiRadioGroup>
							) : (
								<MuiAlert severity="info">No payment providers available</MuiAlert>
							)}
						</Spin>
						<Typography>
							Total amount you will be charged:{' '}
							<span style={{ fontWeight: 'bold' }}>{formatCurrency(invoiceDetailed?.totalAmount, 'AUD')}</span>
						</Typography>
						<FlexRowSpaced style={{ justifyContent: 'flex-end' }}>
							<Button
								disabled={payInvoiceLoading || invoiceDetailedLoading || customerLoading}
								style={{ maxWidth: '100px' }}
								onClick={() => closeDrawer()}
								variant="tertiary"
								color="error"
							>
								Cancel
							</Button>
							<Button
								disabled={!payment || invoiceDetailedLoading || customerLoading || payInvoiceLoading}
								loading={payInvoiceLoading}
								onClick={async () => {
									const transactionRef = moment().format('YYYYMMDD-HHmmss-') + nanoid(5);
									await payInvoice({
										invoiceId: invoice.id,
										paymentInput: {
											contentId,
											currency: 'AUD',
											effectiveDate: dateToIsoDateOnly(new Date()),
											lines: invoiceDetailed?.lineItems?.map(lineItem => {
												return {
													amount: lineItem.taxInclusive ? lineItem.amount : lineItem.amount + lineItem.taxAmount,
													comment: lineItem.comment,
													lineTypeId: 1
													//if we add invoiceId, if payment of the invoice fails,
													//the invoice will fetch lineitems from previously failed attempts,
													//making the amount incorrect causing the invoice to never be able to be paid
													//invoiceId: invoiceDetailed.id
												} as CreateLineItemModelInput;
											}),
											...payment,
											receiptNo: transactionRef,
											transactionRef: transactionRef,
											useLinkedOrganisationData
										}
									});
								}}
								sx={{ maxWidth: '100px' }}
								variant="primary"
							>
								Pay
							</Button>
						</FlexRowSpaced>
					</>
				)}
				{!strIsNullOrEmpty(payInvoiceError) ? <MuiAlert severity="error">{payInvoiceError}</MuiAlert> : null}
				{paymentResult?.success ? <MuiAlert severity="success">Payment complete</MuiAlert> : null}
				{paymentResult?.id && !paymentResult?.success ? <MuiAlert severity="error">Payment failed</MuiAlert> : null}
			</FlexColumnSpaced>
		</Padded>
	);
}
